import { useCallback, useMemo } from "react";
import { FormikProps, getIn } from "formik";
import _ from "lodash";

import theme from "theme";
import { colors } from "theme/palette";

import Alert from "components/Alert";
import Checkbox from "components/Checkbox";
import Divider from "components/Divider";
import FormControlLabel from "components/FormControlLabel";
import FormHelperText from "components/FormHelperText";
import Skeleton from "components/Skeleton";
import Stack from "components/Stack";
import Text from "components/Text";
import TextField from "components/TextField";

import { SectionValuesType } from "entities/MemoTemplate/components/SectionForm";
import { ISourceDocument } from "entities/MemoTemplate/sdk";

export interface IMemoSectionAIPromptForm {
  sourceDocumentIds: Array<number>;
  formik: FormikProps<SectionValuesType>;
  areSourceDocumentsLoading: boolean;
  sourceDocuments?: Array<ISourceDocument>;
}

const MemoSectionAIPromptForm = ({
  sourceDocumentIds,
  formik,
  areSourceDocumentsLoading,
  sourceDocuments,
}: IMemoSectionAIPromptForm) => {
  const handleQuestionChange = useCallback(
    (e) => {
      formik.setFieldValue("ai_prompt.prompt", e.target.value);
    },
    [formik]
  );

  const handleTitleChange = useCallback(
    (e) => {
      formik.setFieldValue("ai_prompt.title", e.target.value);
    },
    [formik]
  );

  const memoSectionSourceDocumentsHandleChange = useCallback(
    (e) => {
      const selectedId = Number(e.target.value);
      const updatedIds = _.xor(
        formik.values.ai_prompt?.source_document_ids || [],
        [selectedId]
      );

      formik.setFieldValue("ai_prompt.source_document_ids", updatedIds);
    },
    [formik]
  );

  const sourceDocumentErrorExists = useMemo(() => {
    return (
      getIn(formik.touched, "ai_prompt.source_document_ids") &&
      _.isEmpty(formik.values.ai_prompt?.source_document_ids) &&
      !_.isNil(getIn(formik.errors, "ai_prompt.source_document_ids"))
    );
  }, [
    formik.errors,
    formik.touched,
    formik.values.ai_prompt?.source_document_ids,
  ]);

  return (
    <>
      <Stack direction="row" gap={1.5} alignItems="center">
        <Text color={colors.blue80} fontSize={11} fontWeight={500}>
          PROMPT
        </Text>
        <Divider
          sx={{
            flex: 1,
            margin: theme.spacing(3, 0),
          }}
        />
      </Stack>
      <>
        <Alert
          severity="warning"
          sx={{ marginBottom: 1, alignItems: "center" }}
        >
          Updating this prompt will update it for all memo templates that
          include it. Be careful!
        </Alert>

        <Text variant="text3" marginBottom={0.5}>
          Prompt title
        </Text>
        <TextField
          fullWidth
          value={formik.values.ai_prompt?.title}
          onChange={handleTitleChange}
          helperText={
            getIn(formik.touched, "ai_prompt.title") &&
            _.isNil(formik.values.ai_prompt?.id) &&
            getIn(formik.errors, "ai_prompt.title")
          }
          error={
            getIn(formik.touched, "ai_prompt.title") &&
            _.isNil(formik.values.ai_prompt?.id) &&
            !_.isEmpty(getIn(formik.errors, "ai_prompt.title"))
          }
        />

        <Text variant="text3" marginTop={4} marginBottom={0.5}>
          AI source documents
        </Text>
        <Stack
          display="grid"
          gridTemplateColumns="repeat(3, 1fr)"
          alignItems="center"
          justifyContent="center"
          gap={1.5}
        >
          {!areSourceDocumentsLoading &&
            sourceDocuments?.map((item, index) => (
              <FormControlLabel
                key={index}
                label={
                  <Text fontWeight={700} fontSize={16}>
                    {item.name}
                  </Text>
                }
                sx={{ alignItems: "flex-start", width: "fit-content" }}
                control={
                  <Checkbox
                    sx={{
                      color: colors.gray40,
                      padding: theme.spacing(0, 0.5, 0.5, 1.5),
                    }}
                    onChange={memoSectionSourceDocumentsHandleChange}
                    value={item.id}
                    checked={sourceDocumentIds.includes(item.id)}
                  />
                }
              />
            ))}
          {areSourceDocumentsLoading &&
            _.range(12).map(() => <Skeleton height={50} />)}
        </Stack>
        <Stack margin={theme.spacing(0, 0, 3, 2)}>
          {sourceDocumentErrorExists && (
            <FormHelperText error>
              {getIn(formik.errors, "ai_prompt.source_document_ids")}
            </FormHelperText>
          )}
        </Stack>

        <Text variant="text3" marginTop={4} marginBottom={0.5}>
          Detailed prompt question
        </Text>
        <TextField
          onChange={handleQuestionChange}
          multiline
          rows={20}
          fullWidth
          value={formik.values.ai_prompt?.prompt}
          helperText={
            getIn(formik.touched, "ai_prompt.prompt") &&
            _.isNil(formik.values.ai_prompt?.id) &&
            getIn(formik.errors, "ai_prompt.prompt")
          }
          error={
            getIn(formik.touched, "ai_prompt.prompt") &&
            _.isNil(formik.values.ai_prompt?.id) &&
            !_.isEmpty(getIn(formik.errors, "ai_prompt.prompt"))
          }
        />
      </>
    </>
  );
};

export default MemoSectionAIPromptForm;
